'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import { ZipCity, type ZipCityLocation } from '@/components/ZipCity';

interface LocationPickerProps {
  /**
   * Label the applicant saw for {@link value}, kept by the step so the input
   * still shows the chosen Standort after leaving and returning to step 2.
   * ZipCity owns the visible text, but this component unmounts with the step,
   * so without a persisted label the field would come back empty while
   * `locationSlugs` still held the slug.
   */
  label?: string;
  /**
   * `centerNumber` is `null` whenever `slug` is: an unresolved input carries
   * neither, and the two must stay in lockstep so the submitted
   * `schoolNumbers` line up with `locationSlugs`.
   */
  onChange: (
    slug: string | null,
    label: string,
    centerNumber: number | null,
  ) => void;
  required?: boolean;
  error?: string;
  /** Id of the error box the step renders above this field. */
  errorId?: string;
}

/**
 * Whether an application may name this Standort. See
 * `LocationSearchResultDTO.centerNumber` in the backend.
 */
const canApply = (loc: ZipCityLocation): boolean =>
  typeof loc.centerNumber === 'number';

/**
 * One Standort search input.
 *
 * Wraps the shared `<ZipCity />` — the same autocomplete the header and the
 * location finder use — rather than a second search implementation, so the
 * magnifier, the `PLZ - Ort` result format, the debouncing and the combobox
 * a11y all come for free.
 */
export const LocationPicker = ({
  label = '',
  onChange,
  required = false,
  error,
  errorId,
}: LocationPickerProps) => {
  const t = useTranslations('tutorApplication');
  const [text, setText] = useState(label);

  const handleTextChange = (next: string) => {
    setText(next);
    // Reported unconditionally, not only when a slug was already set: the step
    // needs to see text that resolved to nothing, otherwise a typed-but-never-
    // selected PLZ is silently discarded. Clearing the slug at the same time
    // keeps a half-typed query from submitting the previously chosen Standort.
    onChange(null, next, null);
  };

  const handleSelect = (loc: ZipCityLocation) => {
    const label = loc.postcode ? `${loc.postcode} - ${loc.name}` : loc.name;
    // `canApply` already keeps these out of the dropdown; refusing the slug
    // here too means a Standort without a `centerNumber` can never reach the
    // payload, whatever else changes.
    if (!canApply(loc)) {
      onChange(null, label, null);
      return;
    }
    onChange(loc.slug, label, loc.centerNumber ?? null);
  };

  return (
    <ZipCity
      variant="formField"
      value={text}
      onValueChange={handleTextChange}
      onSelect={handleSelect}
      required={required}
      placeholder={t('locationPlaceholder')}
      // `error` gives the red border and aria wiring; the message itself is
      // rendered by the step in a box above the field, per the design.
      error={error}
      showErrorText={false}
      errorId={errorId}
      // No "Online Nachhilfe testen!" entry here: it navigates away and would
      // abandon a part-filled application. An empty search says so instead.
      noResultsText={t('locationNoResults')}
      // A Standort with no `centerNumber` has no NHL-Tool `schoolnumber`, so
      // an application naming it would be rejected. Hiding it makes the
      // invalid choice unavailable rather than reporting it after the fact.
      filterResults={canApply}
    />
  );
};
